import React from 'react';
import { Video, Play, Zap, Sparkles, Clock } from 'lucide-react';

interface LoadingScreenProps {
  message?: string;
  submessage?: string;
  type?: 'video' | 'clip' | 'general';
  progress?: number; // 0-100 for determined progress, undefined for indeterminate
}

const LoadingScreen: React.FC<LoadingScreenProps> = ({ 
  message = "Loading...", 
  submessage,
  type = 'general',
  progress
}) => {
  const getIcon = () => {
    switch (type) {
      case 'video':
        return Video;
      case 'clip':
        return Play;
      default:
        return Sparkles;
    }
  };

  const getTimingMessage = () => {
    switch (type) {
      case 'video':
        return "Large videos may take several minutes to process";
      case 'clip':
        return "Processing time depends on video complexity";
      default:
        return "Please wait while we process your request";
    }
  };

  const Icon = getIcon();

  return (
    <div className="min-h-screen flex items-center justify-center relative">
      {/* Animated background */}
      <div className="absolute inset-0 dot-pattern-animated opacity-20 pointer-events-none" />
      
      <div className="text-center relative z-10 max-w-md mx-auto px-6">
        {/* Different animation based on type */}
        {type === 'clip' ? (
          /* Simple spinner for clip loading */
          <div className="relative mb-8">
            <div className="w-16 h-16 mx-auto relative">
              <div className="absolute inset-0 rounded-full border-4 border-primary-100"></div>
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-black animate-spin"></div>
              
              {/* Simple centered icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <Icon size={20} className="text-black" />
              </div>
            </div>
          </div>
        ) : (
          /* Complex animation for video generation */
          <div className="relative mb-8">
            {/* Outer spinning ring */}
            <div className="w-24 h-24 mx-auto relative">
              <div className="absolute inset-0 rounded-full border-4 border-primary-100"></div>
              <div className="absolute inset-0 rounded-full border-4 border-transparent border-t-black animate-spin"></div>
              
              {/* Inner pulsing icon */}
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-12 h-12 bg-black rounded-full flex items-center justify-center animate-pulse-subtle">
                  <Icon size={24} className="text-white" />
                </div>
              </div>
            </div>
            
            {/* Floating dots animation */}
            <div className="absolute -inset-8 pointer-events-none">
              <div className="absolute top-0 left-0 w-2 h-2 bg-black rounded-full animate-bounce" style={{ animationDelay: '0s' }}></div>
              <div className="absolute top-0 right-0 w-2 h-2 bg-black rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
              <div className="absolute bottom-0 left-0 w-2 h-2 bg-black rounded-full animate-bounce" style={{ animationDelay: '0.4s' }}></div>
              <div className="absolute bottom-0 right-0 w-2 h-2 bg-black rounded-full animate-bounce" style={{ animationDelay: '0.6s' }}></div>
            </div>
          </div>
        )}

        {/* Loading text */}
        <div className="space-y-4">
          <h2 className="text-2xl font-display font-bold text-black animate-fade-up">
            {message}
          </h2>
          
          {submessage && (
            <p className="text-primary-600 animate-fade-up" style={{ animationDelay: '0.2s' }}>
              {submessage}
            </p>
          )}
          
          {/* Progress Bar */}
          <div className="w-full animate-fade-up" style={{ animationDelay: '0.3s' }}>
            <div className="w-full bg-primary-100 rounded-lg h-2 overflow-hidden">
              {progress !== undefined ? (
                // Determinate progress bar
                <div 
                  className={`h-full bg-black transition-all duration-300 ease-out ${progress >= 100 ? 'rounded-lg' : 'rounded-l-lg'}`}
                  style={{ width: `${Math.min(100, Math.max(0, progress))}%` }}
                />
              ) : (
                // Indeterminate progress bar
                <div className="h-full bg-black rounded-l-lg progress-indeterminate" />
              )}
            </div>
            
            {progress !== undefined && (
              <div className="flex justify-between text-xs text-primary-500 mt-1">
                <span>Processing...</span>
                <span>{Math.round(progress)}%</span>
              </div>
            )}
          </div>

          {/* Progress indicator dots */}
          <div className="flex justify-center space-x-1 animate-fade-up" style={{ animationDelay: '0.4s' }}>
            <div className="w-2 h-2 bg-black rounded-full animate-pulse" style={{ animationDelay: '0s' }}></div>
            <div className="w-2 h-2 bg-black rounded-full animate-pulse" style={{ animationDelay: '0.2s' }}></div>
            <div className="w-2 h-2 bg-black rounded-full animate-pulse" style={{ animationDelay: '0.4s' }}></div>
            <div className="w-2 h-2 bg-black rounded-full animate-pulse" style={{ animationDelay: '0.6s' }}></div>
            <div className="w-2 h-2 bg-black rounded-full animate-pulse" style={{ animationDelay: '0.8s' }}></div>
          </div>
        </div>

        {/* Activity messages */}
        {type !== 'clip' && (
          <div className="mt-8 space-y-3 animate-fade-up" style={{ animationDelay: '0.6s' }}>
            {type === 'video' && (
              <div className="text-sm text-primary-500 space-y-2">
                <div className="flex items-center justify-center space-x-2 animate-pulse">
                  <Zap size={14} />
                  <span>Analyzing video content...</span>
                </div>
                <div className="text-xs text-primary-400 bg-primary-50 rounded-lg p-3">
                  <div className="flex items-center justify-center space-x-2 mb-1">
                    <Clock size={12} />
                    <span className="font-medium">Processing Time Estimates:</span>
                  </div>
                  <div className="space-y-1">
                    <div>• Small videos (&lt;100MB): 30 seconds - 2 minutes</div>
                    <div>• Medium videos (100MB-500MB): 2-5 minutes</div>
                    <div>• Large videos (&gt;500MB): 5-15 minutes</div>
                  </div>
                </div>
              </div>
            )}
          </div>
        )}

        {/* Realistic timing message */}
        {type !== 'clip' && (
          <div className="mt-6 animate-fade-up" style={{ animationDelay: '0.8s' }}>
            <p className="text-xs text-primary-400 bg-primary-50 rounded-lg p-2">
              💡 {getTimingMessage()}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default LoadingScreen; 